import * as Checkbox from '@radix-ui/react-checkbox';
import { ArrowDown, ArrowUp, Check, Crown, Minus, RotateCcw } from 'lucide-react';
import { useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import type { SortSpec, TicketSummary } from '@aurora/contracts';
import { cn } from '../../lib/cn';
import { absoluteTime, relativeTime } from '../../lib/format';
import { useSession } from '../../lib/session';
import { Avatar, Badge, Button, Skeleton } from '../../components/ui';
import { InlineStatus, type StatusChoice } from './inline-status';
import { InlineAssignee, useAssignToMe } from './list-inline-assignee';
import { SLA_URGENCY_SORT } from './list-filters';
import { SlaDeadline } from './sla-deadline';
import { ticketStatusTone } from './status-tone';

/**
 * The ticket table.
 *
 * This is where agents spend their day, so it is built for scanning:
 *
 *   * A fixed layout. Columns have widths and the subject takes what is left,
 *     so a long subject truncates instead of pushing the table wider than the
 *     page — which it used to do at 1280px, with a sideways scrollbar.
 *   * Columns that matter less give way first as the space narrows, by the
 *     width of the list itself rather than the window, because the sidebar
 *     takes a different share at different sizes.
 *   * The whole row opens the ticket. Ctrl- or ⌘-click and middle-click open
 *     it in a new tab; the checkbox and the in-place menus do not navigate.
 *   * j/k (or the arrow keys) move between rows, Enter opens, x selects.
 *     Shift-click (or Shift-x) selects every row from the last one ticked.
 *     On the focused row, a assigns it to you and s opens its status menu.
 *     The list's "Keyboard shortcuts" button says all of this on screen
 *     (`LIST_SHORTCUTS`), so keep the two in step.
 *   * Selection is by ticket *number*. It used to be by internal id, which the
 *     bulk endpoint read as a number — so once the two drifted apart, an
 *     action on #105 changed #107.
 */

/**
 * Every keyboard shortcut the list answers to, in the order people learn them.
 * Shown on screen by the list's "Keyboard shortcuts" button; the handler is
 * `onKeyDown` below and the search box's "/" — keep the three in step.
 */
export const LIST_SHORTCUTS: ReadonlyArray<{ keys: string[]; does: string }> = [
  { keys: ['/'], does: 'Search the list' },
  { keys: ['#123', 'Enter'], does: 'Typed in search: open that ticket' },
  { keys: ['j', 'k'], does: 'Move down and up the rows (or the arrow keys)' },
  { keys: ['Enter'], does: 'Open the ticket' },
  { keys: ['x'], does: 'Tick the row — with Shift, every row since the last one ticked' },
  { keys: ['a'], does: 'Assign the ticket to me' },
  { keys: ['s'], does: 'Change its status' },
];

export type ColumnKey =
  | 'number'
  | 'subject'
  | 'requester'
  | 'assignee'
  | 'group'
  | 'priority'
  | 'status'
  | 'sla'
  | 'updated';

export interface Column {
  key: ColumnKey;
  label: string;
  /** Maps to a filter-schema field, when the column can be sorted on. */
  sortField?: string;
  /** What the first click on the header asks for. */
  defaultDirection?: 'asc' | 'desc';
  /**
   * The full order a direction stands for, when one field is not enough to say
   * it. Absent, a direction is simply this column's field in that direction.
   */
  sortSpecs?: (direction: 'asc' | 'desc') => SortSpec[];
  /** Header width; the subject has none and takes the rest. */
  width?: string;
  /** Hidden below a width of the list, by container query. */
  responsive?: string;
}

/*
 * Which columns give way, and when. The subject takes whatever the fixed
 * columns leave, so each breakpoint is where the columns shown so far still
 * leave it about 220px or more — the old breakpoints left it under 70px on a
 * 600px-wide list, and nothing at all on a phone. A column that gives way
 * moves its answer under the subject (see `Cell`) — priority too, which on a
 * phone used to vanish altogether.
 *
 *   always            checkbox, subject, status
 *   @md   (28rem)     + number
 *   @2xl  (42rem)     + SLA
 *   @3xl  (48rem)     + priority
 *   @5xl  (64rem)     + agent, updated      (a 1280px window has 65rem)
 *   @7xl  (80rem)     + requester, group    (opt-in columns)
 */
export const ALL_COLUMNS: Column[] = [
  {
    key: 'number',
    label: 'ID',
    sortField: 'id',
    defaultDirection: 'desc',
    width: 'w-16',
    responsive: 'hidden @md:table-cell',
  },
  { key: 'subject', label: 'Subject', sortField: 'subject', defaultDirection: 'asc' },
  {
    key: 'requester',
    label: 'Requester',
    sortField: 'requester_id',
    defaultDirection: 'asc',
    width: 'w-40',
    responsive: 'hidden @7xl:table-cell',
  },
  {
    key: 'assignee',
    label: 'Agent',
    sortField: 'assignee_id',
    defaultDirection: 'asc',
    width: 'w-36',
    responsive: 'hidden @5xl:table-cell',
  },
  {
    key: 'group',
    label: 'Group',
    sortField: 'group_id',
    defaultDirection: 'asc',
    width: 'w-32',
    responsive: 'hidden @7xl:table-cell',
  },
  // Most urgent first: nobody sorts by priority to see the low ones.
  {
    key: 'priority',
    label: 'Priority',
    sortField: 'priority',
    defaultDirection: 'desc',
    width: 'w-24',
    responsive: 'hidden @3xl:table-cell',
  },
  // Wide enough for "Awaiting approval" and its menu chevron, the longest of
  // the shipped statuses; at w-32 it read "Awaiting ap…".
  { key: 'status', label: 'Status', sortField: 'status', defaultDirection: 'asc', width: 'w-44' },
  // Most urgent first: breached, then by the same running deadline the cell
  // shows. The deadline alone put breached tickets — which have none still
  // running — after everything that was merely due.
  {
    key: 'sla',
    label: 'SLA',
    sortField: 'sla_due_at',
    defaultDirection: 'asc',
    sortSpecs: (direction) => (direction === 'asc' ? SLA_URGENCY_SORT : [{ field: 'sla_due_at', direction: 'desc' }]),
    // Wide enough for "2d 23h to resolve" in its badge; narrower cut the
    // target off the at-risk chip, which is the part that says what is due.
    width: 'w-40',
    responsive: 'hidden @2xl:table-cell',
  },
  {
    key: 'updated',
    label: 'Updated',
    sortField: 'last_activity_at',
    defaultDirection: 'desc',
    width: 'w-24',
    responsive: 'hidden @5xl:table-cell',
  },
];

export const DEFAULT_COLUMN_KEYS: ColumnKey[] = ['number', 'subject', 'assignee', 'priority', 'status', 'sla', 'updated'];

/** Only known keys, always the subject, always in the canonical order. */
export function sanitiseColumnKeys(keys: unknown): ColumnKey[] {
  if (!Array.isArray(keys)) return DEFAULT_COLUMN_KEYS;
  const wanted = new Set(keys.filter((key): key is string => typeof key === 'string'));
  const picked = ALL_COLUMNS.filter((column) => wanted.has(column.key) || column.key === 'subject').map(
    (column) => column.key,
  );
  return picked.length > 1 ? picked : DEFAULT_COLUMN_KEYS;
}

/**
 * The columns somebody who raised a ticket sees: what it is, where it has got
 * to in their words, who has it, and when it last moved. No SLA, no queue.
 */
const REQUESTER_COLUMNS: Array<{ key: 'number' | 'subject' | 'status' | 'handler' | 'updated'; label: string; width?: string; responsive?: string }> = [
  { key: 'number', label: 'ID', width: 'w-16', responsive: 'hidden @md:table-cell' },
  { key: 'subject', label: 'Request' },
  // Wide enough for "Awaiting your response", the longest of the shipped
  // requester wordings; it was cut to "Awaiting your resp…" at w-44.
  { key: 'status', label: 'Status', width: 'w-48' },
  { key: 'handler', label: 'Handled by', width: 'w-40', responsive: 'hidden @3xl:table-cell' },
  { key: 'updated', label: 'Last update', width: 'w-32', responsive: 'hidden @xl:table-cell' },
];

export type TableMode = 'agent' | 'requester' | 'trash';

/**
 * How much room each row takes. Compact trims the space around each row and
 * between its two lines — about a quarter more tickets on a screen — and keeps
 * everything a row says.
 */
export type Density = 'comfortable' | 'compact';

const ROW_PADDING: Record<Density, string> = { comfortable: 'py-2.5', compact: 'py-1' };

interface TicketTableProps {
  tickets: TicketSummary[];
  mode: TableMode;
  columns: Column[];
  /** The order somebody asked for by clicking a header. */
  sort: SortSpec[];
  /**
   * The order the list is in when nobody has asked: the view's, or the
   * server's own. Shown, faintly, on its column, so the order is never a
   * mystery.
   */
  defaultSort?: SortSpec[];
  onSortChange: (sort: SortSpec[]) => void;
  /** Whether rows carry a checkbox: only for somebody who can act on a selection. */
  selectable: boolean;
  selected: ReadonlySet<number>;
  onSelectedChange: (selected: Set<number>) => void;
  /** First load, with nothing to show yet. */
  loading?: boolean;
  /** Statuses a ticket can be moved to in place; absent, the cell is a label. */
  statusChoices?: StatusChoice[];
  /** Whether the agent cell can reassign in place. */
  canAssign?: boolean;
  /** Trash only: put one ticket back. */
  onRestore?: (ticket: TicketSummary) => void;
  restoring?: ReadonlySet<number>;
  density?: Density;
}

export function TicketTable({
  tickets,
  mode,
  columns,
  sort,
  defaultSort,
  onSortChange,
  selectable,
  selected,
  onSelectedChange,
  loading,
  statusChoices,
  canAssign,
  onRestore,
  restoring,
  density = 'comfortable',
}: TicketTableProps) {
  const padding = ROW_PADDING[density];
  const navigate = useNavigate();
  const location = useLocation();
  const { can } = useSession();
  const assignToMe = useAssignToMe();
  /** The row whose status menu is open, so the keyboard can open one. */
  const [statusMenuFor, setStatusMenuFor] = useState<number | null>(null);
  /**
   * This list, exactly — view, filters, search, sort and page — handed to the
   * ticket it opens, whose "Back to tickets" returns here rather than to the
   * first page of everything. (A ticket opened in a new tab has no list to go
   * back to, and goes back to the list as it opens.)
   */
  const from = { from: `${location.pathname}${location.search}` };
  const numbers = tickets.map((ticket) => ticket.number);
  const allSelected = numbers.length > 0 && numbers.every((number) => selected.has(number));
  const someSelected = numbers.some((number) => selected.has(number));
  /** The row last ticked on its own: where a Shift-click range starts. */
  const anchor = useRef<number | null>(null);

  const toggleAll = () => {
    const next = new Set(selected);
    if (allSelected) for (const number of numbers) next.delete(number);
    else for (const number of numbers) next.add(number);
    onSelectedChange(next);
  };

  /**
   * Ticks or unticks one row — or, with Shift, every row from the last one
   * ticked to this one, all to this row's new state, as a mail client does.
   * Triaging twenty tickets used to be twenty separate clicks.
   */
  const toggleOne = (number: number, range = false) => {
    const next = new Set(selected);
    const on = !next.has(number);
    const from = range && anchor.current !== null ? numbers.indexOf(anchor.current) : -1;
    const to = numbers.indexOf(number);
    if (from !== -1 && to !== -1) {
      const [start, end] = from < to ? [from, to] : [to, from];
      for (const each of numbers.slice(start, end + 1)) {
        if (on) next.add(each);
        else next.delete(each);
      }
    } else if (on) next.add(number);
    else next.delete(number);
    anchor.current = number;
    onSelectedChange(next);
  };

  const specsFor = (column: Column, direction: 'asc' | 'desc'): SortSpec[] =>
    column.sortSpecs ? column.sortSpecs(direction) : [{ field: column.sortField!, direction }];

  /**
   * First click asks for the column's natural order — most urgent priority,
   * newest update, soonest deadline — then the reverse, then back to the
   * list's own order. A column already showing the list's own order flips it.
   */
  const handleSort = (column: Column) => {
    const field = column.sortField;
    if (!field) return;
    const first = column.defaultDirection ?? 'asc';
    const flip = (direction: 'asc' | 'desc') => (direction === 'asc' ? 'desc' : 'asc');
    const explicit = sort.find((spec) => spec.field === field);
    if (explicit) {
      onSortChange(explicit.direction === first ? specsFor(column, flip(first)) : []);
      return;
    }
    const implicit = sort.length === 0 ? defaultSort?.find((spec) => spec.field === field) : undefined;
    onSortChange(specsFor(column, implicit ? flip(implicit.direction) : first));
  };

  // A ticket in the trash has no page to open — the server hides it until it
  // is restored — so its row is not a link to one; Restore is what it offers.
  const rowsOpen = mode !== 'trash';

  const openTicket = (event: MouseEvent<HTMLTableRowElement>, number: number) => {
    if (!rowsOpen) return;
    // Clicks inside a menu opened from this row arrive here through React's
    // tree even though the menu is portalled elsewhere in the page.
    if (!(event.target instanceof Node) || !event.currentTarget.contains(event.target)) return;
    if (event.target instanceof Element && event.target.closest(INTERACTIVE)) return;
    // Somebody selecting text to copy it has not asked to navigate.
    if (window.getSelection()?.toString()) return;
    const href = `/tickets/${number}`;
    if (event.metaKey || event.ctrlKey || event.button === 1) {
      window.open(href, '_blank', 'noopener');
      return;
    }
    navigate(href, { state: from });
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTableSectionElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const target = event.target as HTMLElement;
    if (!event.currentTarget.contains(target)) return;
    const row = target.closest<HTMLTableRowElement>('tr[data-number]');
    if (!row) return;
    // Typing in a menu or a picker opened from the row is not navigation.
    if (target.closest('[role="menu"], [role="listbox"], input, textarea, select')) return;

    const move = (offset: number) => {
      const rows = [...event.currentTarget.querySelectorAll<HTMLTableRowElement>('tr[data-number]')];
      const next = rows[rows.indexOf(row) + offset];
      next?.querySelector<HTMLElement>('[data-row-link]')?.focus();
    };

    if (event.key === 'ArrowDown' || event.key === 'j') {
      event.preventDefault();
      move(1);
    } else if (event.key === 'ArrowUp' || event.key === 'k') {
      event.preventDefault();
      move(-1);
    } else if ((event.key === 'x' || event.key === 'X') && selectable) {
      event.preventDefault();
      toggleOne(Number(row.dataset.number), event.shiftKey);
    } else if (event.key === 'a' && mode === 'agent' && canAssign) {
      const ticket = tickets.find((candidate) => candidate.number === Number(row.dataset.number));
      if (!ticket) return;
      event.preventDefault();
      assignToMe.run(ticket);
    } else if (event.key === 's' && mode === 'agent' && (statusChoices?.length ?? 0) > 0) {
      event.preventDefault();
      setStatusMenuFor(Number(row.dataset.number));
    }
  };

  const has = (key: ColumnKey) => columns.some((column) => column.key === key);
  // Without a requester column the requester is always under the subject;
  // with one, only while the list is too narrow to show the column.
  const showRequesterLine = mode !== 'requester' && !has('requester');
  const fallbacks = {
    agent: has('assignee'),
    number: has('number'),
    sla: has('sla'),
    priority: has('priority'),
    requester: has('requester'),
    group: has('group'),
  };

  const headers =
    mode === 'requester'
      ? REQUESTER_COLUMNS.map((column) => ({ ...column, sortable: undefined as Column | undefined }))
      : columns.map((column) => ({ ...column, sortable: column.sortField ? column : undefined }));

  return (
    <table className="w-full table-fixed border-collapse text-start" data-density={density}>
      <thead className="sticky top-0 z-10 bg-sunken shadow-[inset_0_-1px_0_var(--color-line)]">
        <tr>
          {selectable && (
            <th scope="col" className="w-9 px-3 py-2">
              <SelectBox
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={toggleAll}
                label={allSelected ? 'Deselect all on this page' : 'Select all on this page'}
                disabled={tickets.length === 0}
              />
            </th>
          )}
          {headers.map((column) => {
            const sortColumn = column.sortable;
            const explicit = sortColumn ? sort.find((spec) => spec.field === sortColumn.sortField) : undefined;
            const implicit =
              !explicit && sortColumn && sort.length === 0
                ? defaultSort?.find((spec) => spec.field === sortColumn.sortField)
                : undefined;
            const active = explicit ?? implicit;
            return (
              <th
                key={column.key}
                scope="col"
                aria-sort={active ? (active.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                className={cn(
                  'px-3 py-2 text-start text-mini font-medium text-ink-muted select-none',
                  column.width,
                  column.responsive,
                )}
              >
                {sortColumn ? (
                  <button
                    type="button"
                    onClick={() => handleSort(sortColumn)}
                    title={implicit ? 'The list’s default order. Click to change it.' : undefined}
                    className="inline-flex max-w-full items-center gap-1 rounded transition-colors hover:text-ink"
                  >
                    <span className="truncate">{column.label}</span>
                    {active ? (
                      active.direction === 'asc' ? (
                        <ArrowUp aria-hidden className={cn('size-3 shrink-0', implicit && 'opacity-50')} />
                      ) : (
                        <ArrowDown aria-hidden className={cn('size-3 shrink-0', implicit && 'opacity-50')} />
                      )
                    ) : (
                      // Fixed width, so the header does not shift when the arrow appears.
                      <span aria-hidden className="size-3 shrink-0" />
                    )}
                  </button>
                ) : (
                  column.label
                )}
              </th>
            );
          })}
          {mode === 'trash' && onRestore && (
            <th scope="col" className="w-28 px-3 py-2">
              <span className="sr-only">Actions</span>
            </th>
          )}
        </tr>
      </thead>

      <tbody onKeyDown={onKeyDown}>
        {loading &&
          tickets.length === 0 &&
          Array.from({ length: 8 }, (_, index) => (
            <tr key={index} className="border-b border-line">
              {selectable && (
                <td className={cn('px-3', density === 'compact' ? 'py-1.5' : 'py-3')}>
                  <Skeleton className="size-3.5" />
                </td>
              )}
              {headers.map((column) => (
                <td key={column.key} className={cn('px-3', density === 'compact' ? 'py-1.5' : 'py-3', column.responsive)}>
                  <Skeleton className={cn('h-3.5', column.key === 'subject' ? 'w-3/4' : 'w-14')} />
                  {column.key === 'subject' && (
                    <Skeleton className={cn('h-3 w-1/3', density === 'compact' ? 'mt-1' : 'mt-1.5')} />
                  )}
                </td>
              ))}
              {mode === 'trash' && onRestore && <td />}
            </tr>
          ))}

        {tickets.map((ticket) => {
          const isSelected = selected.has(ticket.number);
          return (
            <tr
              key={ticket.id}
              data-number={ticket.number}
              onClick={(event) => openTicket(event, ticket.number)}
              onAuxClick={(event) => {
                if (event.button === 1) openTicket(event, ticket.number);
              }}
              className={cn(
                'group border-b border-line transition-colors',
                rowsOpen && 'cursor-pointer',
                isSelected ? 'bg-accent-soft/60' : 'hover:bg-sunken/70',
              )}
            >
              {selectable && (
                <td className={cn('px-3 align-top', padding)}>
                  <SelectBox
                    checked={isSelected}
                    onCheckedChange={(range) => toggleOne(ticket.number, range)}
                    label={`Select ticket ${ticket.number}`}
                  />
                </td>
              )}

              {mode === 'requester'
                ? REQUESTER_COLUMNS.map((column) => (
                    <td key={column.key} className={cn('overflow-hidden px-3 align-top', padding, column.responsive)}>
                      <RequesterCell column={column.key} ticket={ticket} from={from} />
                    </td>
                  ))
                : columns.map((column) => (
                    <td key={column.key} className={cn('overflow-hidden px-3 align-top', padding, column.responsive)}>
                      <Cell
                        column={column.key}
                        ticket={ticket}
                        mode={mode}
                        statusChoices={statusChoices}
                        canAssign={canAssign === true}
                        showRequesterLine={showRequesterLine}
                        fallbacks={fallbacks}
                        from={from}
                        compact={density === 'compact'}
                        statusOpen={statusMenuFor === ticket.number}
                        onStatusOpenChange={(open) => setStatusMenuFor(open ? ticket.number : null)}
                        assigning={assignToMe.pending.has(ticket.number)}
                      />
                    </td>
                  ))}

              {mode === 'trash' && onRestore && (
                <td className={cn('px-3 text-end align-top', density === 'compact' ? 'py-1' : 'py-2')}>
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={restoring?.has(ticket.number)}
                    onClick={() => onRestore(ticket)}
                    aria-label={`Restore ticket ${ticket.number}`}
                    // The row's one action, so j and k move between these.
                    data-row-link
                  >
                    {!restoring?.has(ticket.number) && <RotateCcw aria-hidden className="size-3" />}
                    Restore
                  </Button>
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Anything in a row that does something of its own, so a click on it does not open the ticket. */
const INTERACTIVE =
  'a, button, input, select, textarea, label, [role="checkbox"], [role="menuitem"], [role="combobox"], [data-row-ignore]';

/** Where a ticket opened from the list goes back to: the list, as it was. */
type FromList = { from: string };

function SubjectLink({ ticket, asText = false, from }: { ticket: TicketSummary; asText?: boolean; from?: FromList }) {
  if (asText) {
    return (
      <span dir="auto" title={ticket.subject} className="block truncate text-body font-medium text-ink">
        {ticket.subject}
      </span>
    );
  }
  return (
    <Link
      to={`/tickets/${ticket.number}`}
      state={from}
      data-row-link
      dir="auto"
      title={ticket.subject}
      className="block truncate text-body font-medium text-ink transition-colors hover:text-accent"
    >
      {ticket.subject}
    </Link>
  );
}

function Cell({
  column,
  ticket,
  mode,
  statusChoices,
  canAssign,
  showRequesterLine,
  fallbacks,
  from,
  compact = false,
  statusOpen,
  onStatusOpenChange,
  assigning = false,
}: {
  column: ColumnKey;
  ticket: TicketSummary;
  mode: TableMode;
  statusChoices?: StatusChoice[] | undefined;
  canAssign: boolean;
  showRequesterLine: boolean;
  /** Columns in the table, whose answers move under the subject when the list is too narrow to show them. */
  fallbacks: { agent: boolean; number: boolean; sla: boolean; priority: boolean; requester: boolean; group: boolean };
  from: FromList;
  /** Whether this row's status menu is open — the row's `s` shortcut opens it. */
  statusOpen?: boolean;
  onStatusOpenChange?: (open: boolean) => void;
  /** Being assigned to the viewer by the row's `a` shortcut. */
  assigning?: boolean;
  /** Compact rows: no gap between the subject and the line under it. */
  compact?: boolean;
}) {
  switch (column) {
    case 'number':
      return mode === 'trash' ? (
        <span className="font-mono text-mini text-ink-subtle">{ticket.number}</span>
      ) : (
        <Link
          to={`/tickets/${ticket.number}`}
          state={from}
          // The subject is the row's link for keyboard users; one tab stop per row is enough.
          tabIndex={-1}
          className="font-mono text-mini text-ink-subtle transition-colors hover:text-accent"
        >
          {ticket.number}
        </Link>
      );

    case 'subject':
      return (
        <div className="min-w-0">
          <SubjectLink ticket={ticket} asText={mode === 'trash'} from={from} />
          <div className={cn('flex min-w-0 items-center gap-1.5 text-mini text-ink-subtle', !compact && 'mt-0.5')}>
            {/* Where a column has given way to a narrow list, its answer moves
                here rather than disappearing. Each breakpoint matches the
                column's own (see ALL_COLUMNS). */}
            {fallbacks.number && <span className="shrink-0 font-mono @md:hidden">#{ticket.number}</span>}
            {fallbacks.sla && (
              <span className="inline-flex shrink-0 @2xl:hidden">
                <SlaDeadline ticket={ticket} />
              </span>
            )}
            {fallbacks.priority && (
              // "High priority" rather than "High": beside a deadline and a
              // name, a bare level does not say what it is the level of.
              <span className="inline-flex shrink-0 @3xl:hidden">
                <PriorityPill priority={ticket.priority} phrase />
              </span>
            )}
            {ticket.requester && (showRequesterLine || fallbacks.requester) && (
              <span className={cn('flex min-w-0 items-center gap-1', !showRequesterLine && '@7xl:hidden')}>
                <span dir="auto" className="truncate">
                  {ticket.requester.name}
                </span>
                {/* VIP is a routing and escalation signal, so it earns a marker. */}
                {ticket.requester.is_vip && <Crown aria-label="VIP requester" className="size-3 shrink-0 text-caution" />}
              </span>
            )}
            {fallbacks.agent && mode === 'agent' && (
              <span className="shrink-0 @5xl:hidden">
                {ticket.requester && (showRequesterLine || fallbacks.requester) ? '→ ' : ''}
                {ticket.assignee ? (
                  <span dir="auto">{ticket.assignee.name}</span>
                ) : (
                  <span className="text-caution-ink">Unassigned</span>
                )}
              </span>
            )}
            {fallbacks.group && ticket.group && (
              <span dir="auto" className="max-w-32 shrink-0 truncate @7xl:hidden">
                {ticket.group.name}
              </span>
            )}
            {ticket.tags.slice(0, 2).map((tag) => (
              <span key={tag} dir="auto" className="max-w-24 shrink-0 truncate rounded bg-sunken px-1 text-mini text-ink-muted">
                {tag}
              </span>
            ))}
            {ticket.tags.length > 2 && <span className="shrink-0">+{ticket.tags.length - 2}</span>}
          </div>
        </div>
      );

    case 'requester':
      return ticket.requester ? (
        <div className="flex min-w-0 items-center gap-1.5">
          <Avatar name={ticket.requester.name} size="xs" />
          <span dir="auto" className="truncate text-mini text-ink-muted">
            {ticket.requester.name}
          </span>
          {ticket.requester.is_vip && <Crown aria-label="VIP requester" className="size-3 shrink-0 text-caution" />}
        </div>
      ) : (
        <span className="text-mini text-ink-subtle">—</span>
      );

    case 'assignee':
      if (canAssign && mode === 'agent') return <InlineAssignee ticket={ticket} busy={assigning} />;
      return ticket.assignee ? (
        <div className="flex min-w-0 items-center gap-1.5">
          <Avatar name={ticket.assignee.name} size="xs" />
          <span dir="auto" className="truncate text-mini text-ink-muted">
            {ticket.assignee.name}
          </span>
        </div>
      ) : (
        <Badge tone="caution">Unassigned</Badge>
      );

    case 'group':
      return (
        <span dir="auto" className="block truncate text-mini text-ink-muted">
          {ticket.group?.name ?? '—'}
        </span>
      );

    case 'priority':
      return <PriorityPill priority={ticket.priority} />;

    case 'status':
      return mode === 'trash' ? (
        <Badge tone={ticketStatusTone(ticket.status.category)} dot className="max-w-full">
          <span className="truncate">{ticket.status.label}</span>
        </Badge>
      ) : (
        <InlineStatus
          ticket={ticket}
          choices={statusChoices ?? []}
          {...(onStatusOpenChange ? { open: statusOpen === true, onOpenChange: onStatusOpenChange } : {})}
        />
      );

    case 'sla':
      return <SlaDeadline ticket={ticket} />;

    case 'updated':
      return (
        <span className="text-mini text-ink-muted" title={absoluteTime(ticket.last_activity_at)}>
          {relativeTime(ticket.last_activity_at)}
        </span>
      );

    default:
      return null;
  }
}

function RequesterCell({
  column,
  ticket,
  from,
}: {
  column: (typeof REQUESTER_COLUMNS)[number]['key'];
  ticket: TicketSummary;
  from: FromList;
}) {
  switch (column) {
    case 'number':
      return (
        <Link
          to={`/tickets/${ticket.number}`}
          state={from}
          tabIndex={-1}
          className="font-mono text-mini text-ink-subtle hover:text-accent"
        >
          {ticket.number}
        </Link>
      );
    case 'subject':
      return (
        <div className="min-w-0">
          <SubjectLink ticket={ticket} from={from} />
          {/* On a narrow screen the side columns go; the number and who has it stay. */}
          <p className="mt-0.5 truncate text-mini text-ink-subtle @3xl:hidden">
            <span className="font-mono @md:hidden">#{ticket.number} · </span>
            {ticket.assignee ? <span dir="auto">With {ticket.assignee.name}</span> : 'Waiting to be picked up'}
          </p>
        </div>
      );
    case 'status':
      return (
        <Badge tone={ticketStatusTone(ticket.status.category)} dot className="max-w-full">
          {/* The requester's own wording: "Awaiting your reply", not "Pending". */}
          <span className="truncate">{ticket.status.requester_label ?? ticket.status.label}</span>
        </Badge>
      );
    case 'handler':
      return ticket.assignee ? (
        <div className="flex min-w-0 items-center gap-1.5">
          <Avatar name={ticket.assignee.name} size="xs" />
          <span dir="auto" className="truncate text-mini text-ink-muted">
            {ticket.assignee.name}
          </span>
        </div>
      ) : (
        <span className="text-mini text-ink-subtle">Waiting to be picked up</span>
      );
    case 'updated':
      return (
        <span className="text-mini text-ink-muted" title={absoluteTime(ticket.last_activity_at)}>
          {relativeTime(ticket.last_activity_at)}
        </span>
      );
    default:
      return null;
  }
}

export function PriorityPill({
  priority,
  phrase = false,
}: {
  priority: TicketSummary['priority'];
  /** "High priority" rather than "High", for a place that does not say it is a priority. */
  phrase?: boolean;
}) {
  // Bars rather than colour alone: four levels are distinguishable by length
  // even in greyscale.
  return (
    <span className="inline-flex max-w-full items-center gap-1.5" title={`Priority: ${priority.label}`}>
      <span aria-hidden className="flex h-3 shrink-0 items-end gap-px">
        {[1, 2, 3, 4].map((level) => (
          <span
            key={level}
            className={cn('w-0.5 rounded-full', level <= barLevel(priority) ? 'bg-ink-muted' : 'bg-line')}
            style={{
              height: `${level * 25}%`,
              ...(level <= barLevel(priority) && priority.colour ? { backgroundColor: priority.colour } : {}),
            }}
          />
        ))}
      </span>
      <span className="truncate text-mini text-ink-muted">{phrase ? `${priority.label} priority` : priority.label}</span>
    </span>
  );
}

/** The shipped priorities are levels 1 to 4; anything an administrator adds is clamped to the bars. */
function barLevel(priority: TicketSummary['priority']): number {
  const byKey: Record<string, number> = { low: 1, medium: 2, high: 3, urgent: 4 };
  return byKey[priority.key] ?? Math.max(1, Math.min(4, priority.level));
}

function SelectBox({
  checked,
  onCheckedChange,
  label,
  disabled,
}: {
  checked: boolean | 'indeterminate';
  /** Told whether Shift was held, for range selection. */
  onCheckedChange: (shift: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <Checkbox.Root
      checked={checked}
      // Handled on the click itself, which knows whether Shift was held; the
      // box's own toggle is skipped so the change is made exactly once.
      onClick={(event) => {
        event.preventDefault();
        onCheckedChange(event.shiftKey);
      }}
      aria-label={label}
      disabled={disabled}
      className={cn(
        'flex size-4 shrink-0 items-center justify-center rounded-[3px] border transition-colors',
        'border-line-strong bg-surface disabled:opacity-50',
        'data-[state=checked]:border-accent data-[state=checked]:bg-accent',
        'data-[state=indeterminate]:border-accent data-[state=indeterminate]:bg-accent',
      )}
    >
      <Checkbox.Indicator className="text-white">
        {checked === 'indeterminate' ? (
          <Minus aria-hidden className="size-3" strokeWidth={3} />
        ) : (
          <Check aria-hidden className="size-3" strokeWidth={3} />
        )}
      </Checkbox.Indicator>
    </Checkbox.Root>
  );
}
